import type {
  PublicStatusBucketRecord,
  PublicStatusChannelRecord,
  PublicStatusResponse,
  PublicStatusState,
  PublicStatusVendorGroup,
} from '@nadoutong/contracts';
import { useLocale, useT } from '../i18n/index.ts';

const DISPLAY_BUCKET_COUNT = 72;

interface Props {
  readonly data: PublicStatusResponse | null;
  readonly loading: boolean;
  readonly error: boolean;
}

export function StatusPage({ data, loading, error }: Props): JSX.Element {
  const { locale } = useLocale();
  const dateTimeFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      data-testid="status-page"
      className="mx-auto max-w-[1210px] px-4 py-7 sm:px-6 lg:py-8"
    >
      {loading ? (
        <StatusLoading />
      ) : error || data === null ? (
        <StatusError />
      ) : (
        <div className="space-y-3">
          <StatusHeader
            data={data}
            timeFormatter={timeFormatter}
            locale={locale}
          />

          <section className="space-y-7" data-testid="status-channels">
            {data.vendors.map((vendor) => (
              <VendorGroupCard
                key={vendor.vendor}
                vendor={vendor}
                dateTimeFormatter={dateTimeFormatter}
              />
            ))}
          </section>
        </div>
      )}
    </div>
  );
}

function StatusHeader(params: {
  readonly data: PublicStatusResponse;
  readonly timeFormatter: Intl.DateTimeFormat;
  readonly locale: string;
}): JSX.Element {
  const { data, locale, timeFormatter } = params;
  const t = useT();

  return (
    <section
      data-testid="status-summary"
      className="ghost rounded-[18px] bg-white/72 px-6 py-6 shadow-warm-sm sm:px-8"
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-[30px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[36px]">
              {t('status.header.title')}
            </h1>
          </div>
          <p className="mt-4 text-[15px] leading-[1.7] text-ink-muted">
            {t('status.summary.window', {
              hours: String(data.window_hours),
              minutes: String(data.bucket_minutes),
            })}
          </p>
        </div>

        <div className="flex flex-col gap-5 lg:items-end">
          <div className="flex items-center gap-4">
            <p className="text-[14px] leading-none text-ink">
              {data.updated_at === null
                ? t('status.summary.updated_pending')
                : t('status.summary.updated_recent', {
                    time: timeFormatter.format(new Date(data.updated_at)),
                    age: formatAge(data.updated_at, locale),
                  })}
            </p>
            <button
              type="button"
              aria-label={t('status.action.refresh')}
              className="ghost-strong flex h-11 w-11 items-center justify-center rounded-[12px] bg-white/70 text-ink transition hover:bg-butter"
              onClick={() => window.location.reload()}
            >
              <RefreshIcon />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px] text-ink">
            <SummaryInlineMetric
              state="healthy"
              label={t('status.state.healthy')}
              value={data.summary.healthy_channels}
            />
            <InlineDivider />
            <SummaryInlineMetric
              state="busy"
              label={t('status.state.busy')}
              value={data.summary.busy_channels}
            />
            <InlineDivider />
            <SummaryInlineMetric
              state="unavailable"
              label={t('status.state.unavailable')}
              value={data.summary.unavailable_channels}
            />
            <InlineDivider />
            <span>
              {t('status.summary.total_inline', {
                count: formatCount(data.summary.total_channels),
              })}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatusLoading(): JSX.Element {
  return (
    <div className="space-y-3" data-testid="status-loading">
      <div className="h-36 animate-pulse rounded-[18px] bg-white/70" />
      <div className="space-y-0">
        <div className="h-28 animate-pulse rounded-[18px] bg-white/80" />
        <div className="-mt-px h-28 animate-pulse rounded-[18px] bg-white/80" />
        <div className="-mt-px h-28 animate-pulse rounded-[18px] bg-white/80" />
      </div>
    </div>
  );
}

function StatusError(): JSX.Element {
  const t = useT();
  return (
    <section
      data-testid="status-error"
      className="ghost rounded-[18px] bg-butter/50 p-7 text-ink"
    >
      <h1 className="mb-3 font-display text-[28px] font-semibold">
        {t('status.error.title')}
      </h1>
      <p className="text-[14px] leading-[1.7] text-ink-muted">
        {t('status.error.body')}
      </p>
    </section>
  );
}

function VendorGroupCard(params: {
  readonly vendor: PublicStatusVendorGroup;
  readonly dateTimeFormatter: Intl.DateTimeFormat;
}): JSX.Element {
  const { dateTimeFormatter, vendor } = params;
  const t = useT();

  return (
    <section data-testid={`status-vendor-${vendor.vendor}`} className="space-y-2.5">
      <div className="flex items-end justify-between px-2 sm:px-4">
        <h2 className="font-display text-[24px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[26px]">
          {vendor.vendor_display_name}
        </h2>
        <span className="text-[13px] text-ink-muted">
          {t('status.vendor.channel_count', {
            count: formatCount(vendor.total_channels),
          })}
        </span>
      </div>

      <article className="ghost rounded-[20px] bg-white/76 px-5 py-5 shadow-warm-sm sm:px-7">
        <div className="divide-y divide-ink/10">
          {vendor.channels.map((channel) => (
            <ChannelRow
              key={channel.channel_id}
              channel={channel}
              dateTimeFormatter={dateTimeFormatter}
            />
          ))}
        </div>
      </article>
    </section>
  );
}

function ChannelRow(params: {
  readonly channel: PublicStatusChannelRecord;
  readonly dateTimeFormatter: Intl.DateTimeFormat;
}): JSX.Element {
  const { channel, dateTimeFormatter } = params;

  return (
    <div
      data-testid={`status-channel-${channel.channel_id}`}
      className="grid gap-4 py-4 lg:grid-cols-[170px_minmax(0,1fr)] lg:items-start"
    >
      <div className="min-w-0">
        <h3 className="font-display text-[18px] font-semibold leading-tight tracking-[-0.01em] text-ink sm:text-[20px]">
          {channel.channel_display_name}
        </h3>
      </div>

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_112px] sm:items-start">
        <StatusMatrix
          channel={channel}
          dateTimeFormatter={dateTimeFormatter}
        />

        <div
          data-testid={`status-channel-availability-${channel.channel_id}`}
          className="flex items-end justify-between gap-4 sm:block sm:pt-0.5 sm:text-right"
        >
          <div>
            <p
              className={[
                'text-[24px] font-semibold leading-none tracking-[-0.03em]',
                stateTextClass(channel.state),
              ].join(' ')}
            >
              {formatPct(channel.availability_pct)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusMatrix(params: {
  readonly channel: PublicStatusChannelRecord;
  readonly dateTimeFormatter: Intl.DateTimeFormat;
}): JSX.Element {
  const { channel, dateTimeFormatter } = params;
  const t = useT();
  const history = compactHistory(channel.history ?? [], DISPLAY_BUCKET_COUNT);

  return (
    <div className="min-w-0">
      <div className="overflow-x-auto pb-1">
        <div
          className="grid min-w-[320px] gap-[3px]"
          style={{
            gridTemplateColumns: `repeat(${Math.max(history.length, 1)}, minmax(2px, 1fr))`,
          }}
          aria-label={t('status.channel.history_label', {
            channel: channel.channel_display_name,
          })}
          data-testid={`status-channel-matrix-${channel.channel_id}`}
        >
          {history.map((bucket) => (
            <BucketBar
              key={bucket.bucket_start}
              bucket={bucket}
              dateTimeFormatter={dateTimeFormatter}
            />
          ))}
        </div>
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-ink-subtle">
        <span>{t('status.matrix.start')}</span>
        <span>{t('status.matrix.now')}</span>
      </div>
    </div>
  );
}

function BucketBar(params: {
  readonly bucket: PublicStatusBucketRecord;
  readonly dateTimeFormatter: Intl.DateTimeFormat;
}): JSX.Element {
  const { bucket, dateTimeFormatter } = params;
  const title = `${dateTimeFormatter.format(new Date(bucket.bucket_start))} · ${formatPct(
    bucket.success_rate_pct,
  )}% · ${formatCount(bucket.request_count)} req`;
  return (
    <span
      title={title}
      className={`block h-[18px] rounded-full ${stateBarClass(bucket.state)}`}
    />
  );
}

function SummaryInlineMetric(params: {
  readonly state: PublicStatusState;
  readonly label: string;
  readonly value: number;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2">
      <StateDot state={params.state} />
      <span>{params.label}</span>
      <strong className="font-semibold text-ink">{formatCount(params.value)}</strong>
    </span>
  );
}

function InlineDivider(): JSX.Element {
  return <span className="hidden h-7 w-px bg-ink/18 sm:inline-block" aria-hidden="true" />;
}

function StateDot({ state }: { readonly state: PublicStatusState }): JSX.Element {
  return <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${stateDotClass(state)}`} />;
}

function RefreshIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M20 11a8 8 0 1 0-2.34 5.66" />
      <path d="M20 4v7h-7" />
    </svg>
  );
}

function stateDotClass(state: PublicStatusState): string {
  switch (state) {
    case 'healthy':
      return 'bg-status-healthy';
    case 'busy':
      return 'bg-status-busy';
    case 'unavailable':
      return 'bg-status-unavailable';
  }
}

function stateTextClass(state: PublicStatusState): string {
  switch (state) {
    case 'healthy':
      return 'text-status-healthy';
    case 'busy':
      return 'text-status-busy';
    case 'unavailable':
      return 'text-status-unavailable';
  }
}

function stateBarClass(state: PublicStatusState): string {
  switch (state) {
    case 'healthy':
      return 'bg-status-healthy';
    case 'busy':
      return 'bg-status-busy';
    case 'unavailable':
      return 'bg-status-unavailable';
  }
}

function compactHistory(
  history: readonly PublicStatusBucketRecord[],
  maxBuckets: number,
): readonly PublicStatusBucketRecord[] {
  if (history.length <= maxBuckets) {
    return history;
  }

  const groupSize = Math.ceil(history.length / maxBuckets);
  const compacted: PublicStatusBucketRecord[] = [];
  for (let index = 0; index < history.length; index += groupSize) {
    const group = history.slice(index, index + groupSize);
    const requestCount = group.reduce((sum, bucket) => sum + bucket.request_count, 0);
    const successRate =
      group.reduce((sum, bucket) => sum + bucket.success_rate_pct, 0) / group.length;
    const first = group[0];
    const last = group[group.length - 1];
    if (first === undefined || last === undefined) {
      continue;
    }
    compacted.push({
      bucket_start: first.bucket_start,
      bucket_end: last.bucket_end,
      success_rate_pct: successRate,
      request_count: requestCount,
      state: worstState(group.map((bucket) => bucket.state)),
    });
  }
  return compacted;
}

function worstState(states: readonly PublicStatusState[]): PublicStatusState {
  if (states.includes('unavailable')) {
    return 'unavailable';
  }
  if (states.includes('busy')) {
    return 'busy';
  }
  return 'healthy';
}

function formatAge(isoDate: string, locale: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.max(0, Math.round(diffMs / 60_000));
  if (locale === 'zh-CN') {
    return `${minutes} 分钟前`;
  }
  return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
}

function formatPct(value: number): string {
  return value.toFixed(1);
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}
